import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
/**
 * This component is a wrapper of vertical menus which listens to keydown events of children
 * and handles up/down arrow key navigation
 */
export var MenuArrowKeyNavigationProvider = function MenuArrowKeyNavigationProvider(_ref) {
  var children = _ref.children,
    handleClose = _ref.handleClose,
    disableArrowKeyNavigation = _ref.disableArrowKeyNavigation,
    keyDownHandlerContext = _ref.keyDownHandlerContext,
    closeOnTab = _ref.closeOnTab,
    onSelection = _ref.onSelection;
  var wrapperRef = useRef(null);
  var _useState = useState(-1),
    _useState2 = _slicedToArray(_useState, 2),
    currentSelectedItemIndex = _useState2[0],
    setCurrentSelectedItemIndex = _useState2[1];
  var incrementIndex = useCallback(function (list) {
    var currentIndex = currentSelectedItemIndex;
    var nextIndex = (currentIndex + 1) % list.length;
    // Skips disabled items. Previously this function relied on a list of enabled elements which caused a
    // difference between currentIndex and the item index in the menu.
    while (nextIndex !== currentIndex && list[nextIndex].getAttribute('aria-disabled') === 'true') {
      nextIndex = (nextIndex + 1) % list.length;
    }
    setCurrentSelectedItemIndex(nextIndex);
    return nextIndex;
  }, [currentSelectedItemIndex]);
  var decrementIndex = useCallback(function (list) {
    var currentIndex = currentSelectedItemIndex;
    var nextIndex = (list.length + currentIndex - 1) % list.length;
    while (nextIndex !== currentIndex && list[nextIndex].getAttribute('aria-disabled') === 'true') {
      nextIndex = (list.length + nextIndex - 1) % list.length;
    }
    setCurrentSelectedItemIndex(nextIndex);
    return nextIndex;
  }, [currentSelectedItemIndex]);
  useLayoutEffect(function () {
    if (disableArrowKeyNavigation) {
      return;
    }

    /**
     * To handle the key events on the list
     * @param event
     */
    var handleKeyDown = function handleKeyDown(event) {
      var _wrapperRef$current;
      var targetElement = event.target;

      //Tab key on menu items can be handled in the parent components of dropdown menus with KeydownHandlerContext
      if (event.key === 'Tab' && closeOnTab) {
        handleClose(event);
        keyDownHandlerContext === null || keyDownHandlerContext === void 0 ? void 0 : keyDownHandlerContext.handleTab();
        return;
      }

      //To trap the focus inside the toolbar using left and right arrow keys
      var focusableElements = getFocusableElements(wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current);
      if (!focusableElements || (focusableElements === null || focusableElements === void 0 ? void 0 : focusableElements.length) === 0) {
        return;
      }
      if (!((_wrapperRef$current = wrapperRef.current) !== null && _wrapperRef$current !== void 0 && _wrapperRef$current.contains(targetElement))) {
        setCurrentSelectedItemIndex(-1);
      }
      switch (event.key) {
        case 'ArrowDown':
          {
            var _focusableElements$fo;
            var focusIndex = incrementIndex(focusableElements);
            (_focusableElements$fo = focusableElements[focusIndex]) === null || _focusableElements$fo === void 0 ? void 0 : _focusableElements$fo.focus();
            event.preventDefault();
            break;
          }
        case 'ArrowUp':
          {
            var _focusableElements$_f;
            var _focusIndex = decrementIndex(focusableElements);
            (_focusableElements$_f = focusableElements[_focusIndex]) === null || _focusableElements$_f === void 0 ? void 0 : _focusableElements$_f.focus();
            event.preventDefault();
            break;
          }

        //ArrowLeft/Right on the menu should close the menus
        //then logic to retain the focus can be handled in the parent components with KeydownHandlerContext
        case 'ArrowLeft':
          //Filter out the events from outside the menu
          if (!targetElement.closest('.custom-key-handler-wrapper')) {
            return;
          }
          if (!targetElement.closest('[data-testid="editor-floating-toolbar"]')) {
            keyDownHandlerContext === null || keyDownHandlerContext === void 0 ? void 0 : keyDownHandlerContext.handleArrowLeft();
          }
          handleClose(event);
          break;
        case 'ArrowRight':
          //Filter out the events from outside the menu
          if (!targetElement.closest('.custom-key-handler-wrapper')) {
            return;
          }
          if (!targetElement.closest('[data-testid="editor-floating-toolbar"]')) {
            keyDownHandlerContext === null || keyDownHandlerContext === void 0 ? void 0 : keyDownHandlerContext.handleArrowRight();
          }
          handleClose(event);
          break;
        case 'Escape':
          handleClose(event);
          break;
        case 'Enter':
          if (typeof onSelection === 'function') {
            onSelection(currentSelectedItemIndex);
          }
          break;
        default:
          return;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSelectedItemIndex, wrapperRef, handleClose, disableArrowKeyNavigation, keyDownHandlerContext, closeOnTab, onSelection, incrementIndex, decrementIndex]);
  return /*#__PURE__*/React.createElement("div", {
    className: "menu-key-handler-wrapper custom-key-handler-wrapper",
    ref: wrapperRef
  }, children);
};
function getFocusableElements(rootNode) {
  if (!rootNode) {
    return [];
  }
  var focusableModalElements = rootNode.querySelectorAll('a[href], button:not([disabled]), textarea, input, select, div[tabindex="-1"]') || [];
  return Array.from(focusableModalElements);
}